import React from 'react';
import clsx from 'clsx';
import { Edit, UserPlus, Info } from 'lucide-react';
import Button from '@components/common/Button';
import Tooltip from '@components/common/Tooltip';
import { ScheduleItem } from '@app-types';
import { UserPermissions } from '@contexts/AuthContext';
import styles from './SchedulerTableCells.module.css';

// Helper function
export const hasPendingDataEnrichment = (scheduleItem: ScheduleItem): boolean => {
  return scheduleItem.contracts.some((c) => c.state === 'pending_data_enrichment');
};

export const canEditRoles = (scheduleItem: ScheduleItem, roleType?: 'owner' | 'manager' | 'assessor'): boolean => {
  // Cannot edit roles if meeting is scheduled or rescheduled
  const isScheduledOrRescheduled =
    scheduleItem.meeting?.status === 'scheduled' || scheduleItem.meeting?.status === 'rescheduled';
  if (isScheduledOrRescheduled) {
    return false;
  }

  // Contract Owners can be edited for non-scheduled meetings
  if (roleType === 'owner') {
    return true;
  }

  // For managers and assessors, only allow editing if meeting is not scheduled or has pending data enrichment
  const notScheduled = !scheduleItem.meeting?.scheduled_datetime;
  return hasPendingDataEnrichment(scheduleItem) || notScheduled;
};

// Reusable Role Assignment Button Component
export const RoleAssignmentButton: React.FC<{
  hasAssignees: boolean;
  onClick: () => void;
  className?: string;
  dataTestId?: string;
}> = ({ hasAssignees, onClick, className, dataTestId }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onClick}
    className={className || styles.roleAssignBtn}
    data-test-id={dataTestId}
  >
    {hasAssignees ? <Edit size={14} /> : <UserPlus size={14} />}
  </Button>
);

// Reusable Role Display Component
const RoleDisplay: React.FC<{
  assignees: { id: number; name: string }[];
  isChildRow?: boolean;
  roleType?: 'manager' | 'owner';
  isAssessor?: boolean;
  hasConflict?: boolean;
}> = ({ assignees, isChildRow, roleType, isAssessor, hasConflict }) => {
  const hasAssignees = assignees.length > 0;

  let displayText = 'Not Assigned';
  if (hasAssignees) {
    displayText = assignees.length === 1 ? assignees[0].name : `${assignees[0].name} +${assignees.length - 1}`;
  }

  // Show dash for child row managers ONLY if no conflict
  if (isChildRow && roleType === 'manager' && !hasConflict) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // Show dash for child row assessors
  if (isChildRow && isAssessor) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // Show info icon for missing owner
  if (!hasAssignees && roleType === 'owner') {
    return (
      <span className={clsx(styles.noDate, styles.missingOwner)}>
        Not Assigned
        <Tooltip
          content="CO not required for meeting, but needed for accurate division reporting"
          position="top"
          variant="warning"
          gap={36}
        >
          <span className={styles.infoIcon}>
            <Info size={18} />
          </span>
        </Tooltip>
      </span>
    );
  }

  return <>{hasAssignees ? displayText : <span className={styles.noDate}>Not Assigned</span>}</>;
};

const MultiRoleCell: React.FC<{
  assignees: { id: number; name: string }[];
  roleType: 'owner' | 'manager';
  scheduleItem: ScheduleItem;
  contractId: number;
  permissions: UserPermissions;
  onAssignRole: (
    item: ScheduleItem,
    contractId: number,
    role: 'owner' | 'manager' | 'assessor',
    isGroupOperation?: boolean
  ) => void;
  isChildRow?: boolean;
  isGroupRow?: boolean;
  hasConflict?: boolean;
}> = ({
  assignees,
  roleType,
  scheduleItem,
  contractId,
  permissions,
  onAssignRole,
  isChildRow,
  isGroupRow,
  hasConflict
}) => {
  const hasAssignees = assignees.length > 0;
  const showButton = roleType === 'manager' ? !isChildRow : true;

  // Parent row in a bundle = group operation
  const isGroupOperation = isGroupRow && !isChildRow;

  return (
    <div className={styles[`${roleType}Cell` as keyof typeof styles]}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName} title={hasAssignees ? assignees.map((a) => a.name).join(', ') : undefined}>
          <RoleDisplay assignees={assignees} isChildRow={isChildRow} roleType={roleType} hasConflict={hasConflict} />
        </span>
        {permissions.canAccessScheduler && canEditRoles(scheduleItem, roleType) && showButton && (
          <RoleAssignmentButton
            hasAssignees={hasAssignees}
            onClick={() => onAssignRole(scheduleItem, contractId, roleType, isGroupOperation)}
            dataTestId={`role-assign-btn-${roleType}`}
          />
        )}
      </div>
    </div>
  );
};

export default MultiRoleCell;
